import {type DuelBeat} from '../sim';
import {duelTiming} from './duelPresentation';
import {superVisualBeats,isSuperAction as isWeaponSuper,type SuperVisualBeat} from '../../shared/weapon-motion';
import type {SuperAction} from '../../shared/weapon-motion';
import {abilityEventAt} from './duelPlayback';
import type {ElementInk,ParticleKind,Vec3,VfxApi} from '../../shared';

export interface DuelVisualCue {
  at:number;side:0|1;kind:'cut'|'parry'|'heal'|'charge'|'super';source?:SuperVisualBeat;
}

export interface AbilityBurst {kind:ParticleKind;point:Vec3;opts:Parameters<VfxApi['burst']>[2];delayMs?:number}
export interface AbilityEffectPlan {bursts:AbilityBurst[];kick:number;punch:number;flare:boolean;impact:boolean}
interface EffectGeometry {point:Vec3;hand:Vec3;along:Vec3;rotation:number;seed:number;project:(p:Vec3)=>{x:number;y:number}}
/** Different silhouettes, colours and rhythms, using the existing bounded
 * particle pool. Delays stay on the presentation clock, never JS timers. */
export function techniqueEffectPlan(action:SuperAction,source:SuperVisualBeat,g:EffectGeometry):AbilityEffectPlan|null {
 if(action==='starfall'||action==='thousandCuts')return null;
 const plan:AbilityEffectPlan={bursts:[],kick:0,punch:0,flare:false,impact:false};
 const {point,hand,along,rotation,seed}=g,ground:Vec3=[point[0],.025,point[2]];
 const add=(kind:ParticleKind,at:Vec3,scale:number,element:ElementInk,count=1,extra:Partial<AbilityBurst['opts']>={},delayMs=0)=>{
  plan.bursts.push({kind,point:[...at],opts:{seed:seed+plan.bursts.length*13,count,scale,element,...extra},...(delayMs?{delayMs}:{})});
 };
 const line=(a:Vec3,b:Vec3,element:ElementInk,lifeMs:number,delayMs=0)=>{
  const pa=g.project(a),pb=g.project(b),mid=a.map((v,i)=>(v+b[i])/2) as Vec3;
  add('cutLine',mid,Math.hypot(...b.map((v,i)=>v-a[i]))/.84,element,1,{rotation:Math.atan2(-(pb.y-pa.y),pb.x-pa.x),lifeMs},delayMs);
 };
 const glyph=(at:Vec3,radius:number,delayMs=0,lifeMs=680)=>{
  // A complete hollow stamp holds long enough to read between the bolts.
  // One bounded particle replaces seven subpixel, separately drawn cuts.
  add('sigilSeal',at,radius/.29,'verdigris',1,{lifeMs},delayMs);
 };
 const decorative=source.damage===false;
 if(action==='solarCleave'){
  if(decorative){add(source.kind==='land'?'dust':'glint',source.kind==='land'?ground:hand,.5,'gold',6);return plan;}
  add('slashArc',point,2.45,'gold',1,{rotation,lifeMs:430});
  add('cutLine',point,source.size||1.8,'minium',1,{rotation,lifeMs:720});
  add('impactRay',point,.88,'gold',8,{dir:along,lifeMs:520});
  add('splat',point,.42,'ink',7,{dir:along,lifeMs:550});
  add('splat',point,.25,'minium',3,{dir:[along[0],along[1]+.4,along[2]],lifeMs:430});
  plan.kick=3;plan.punch=4;plan.flare=!!source.flare;plan.impact=!!source.flare;
 }else if(action==='seismicSlam'){
  if(decorative){add(source.kind==='land'?'dust':'glint',source.kind==='land'?ground:hand,.65,'minium',5);return plan;}
  const slam=source.kind==='slam',scale=source.size||1.8;
  const origin=superVisualBeats(action).find(b=>b.kind==='slam')!.at;
  const impact:Vec3=[point[0]+origin[0]-source.at[0],.025,point[2]-origin[2]+source.at[2]];
  add('impactRing',impact,scale,'minium',1,{lifeMs:720});
  add('dust',impact,slam?1.1:.65,'gold',slam?12:7,{dir:[1,.12,0],lifeMs:580});
  // Eight angular fissures radiate over the floor, then extend with each
  // shock. They are geometry-derived lines, not a screen-covering flash.
  const reach=slam?.65:Math.min(1.35,scale*.37),inner=slam?.08:reach*.46;
  for(let n=0;n<8;n++){
   const angle=n*Math.PI/4+.17,angle2=angle+(n%2?-.16:.16);
   const a:Vec3=[impact[0]+Math.cos(angle)*inner,.035,impact[2]+Math.sin(angle)*inner];
   const b:Vec3=[impact[0]+Math.cos(angle2)*reach*.7,.035,impact[2]+Math.sin(angle2)*reach*.7];
   const c:Vec3=[impact[0]+Math.cos(angle)*reach,.035,impact[2]+Math.sin(angle)*reach];
   line(a,b,'ink',slam?1100:650,n*9);line(b,c,'minium',slam?850:500,n*9+25);
   if(slam)add('impactRay',c,.4,'gold',1,{dir:[Math.cos(angle),.24,Math.sin(angle)],lifeMs:500},n*9+25);
  }
  if(slam)add('impactRay',[impact[0],impact[1]+.12,impact[2]],1.1,'gold',10,{dir:[0,1,0],lifeMs:500});
  plan.kick=slam?5:2.2;plan.punch=slam?6:0;plan.impact=slam;
 }else if(action==='tempest'){
  if(decorative){
   if(source.kind==='land'){add('dust',ground,.45,'lapis',4);return plan;}
   // A large hollow seal gives the summon one readable silhouette, with a
   // rising four-glyph helix rather than many barely visible specks.
   const seal:Vec3=[hand[0],hand[1]-.12,hand[2]+.025];
   glyph(seal,.34,0,1250);
   for(let n=0;n<4;n++){const a=n*2.2,r=.28;
    glyph([hand[0]+Math.cos(a)*r,hand[1]-.4+n*.14,hand[2]+Math.sin(a)*r],.105,n*90,820);}
   add('impactRing',[hand[0],.025,hand[2]],1.5,'verdigris',1,{lifeMs:1050});return plan;
  }
  if(source.kind==='slam'){
   add('impactRing',ground,2.2,'verdigris',1,{lifeMs:780});glyph([point[0],point[1]+.23,point[2]],.34);
   add('impactRay',point,.85,'verdigris',8,{dir:[0,1,0]});plan.punch=3;plan.kick=2;
  }else{
   glyph(point,.24,0,680);
   // Alternating jagged main segments and short forks read as electricity.
   // Every branch has the same contact time and finite lifetime as the bolt.
   const vertices:Vec3[]=[];
   for(let n=0;n<=6;n++){const k=n/6,j=n===0||n===6?0:(n%2?1:-1)*.1;
    vertices.push([hand[0]+(point[0]-hand[0])*k,hand[1]+(point[1]-hand[1])*k+j,hand[2]+(point[2]-hand[2])*k]);}
   for(let n=0;n<6;n++)line(vertices[n],vertices[n+1],n%2?'gold':'verdigris',430);
   for(const n of [2,4]){const a=vertices[n],sign=n===2?1:-1;
    const b:Vec3=[a[0]+.08,a[1]+sign*.16,a[2]+.035],c:Vec3=[b[0]+.11,b[1]+sign*.06,b[2]];
    line(a,b,'verdigris',340);line(b,c,'verdigris',290);}
   add('impactRay',point,.62,'verdigris',6,{dir:along,lifeMs:350});plan.kick=1.1;
  }
 }else{
  if(decorative){add(source.kind==='land'?'dust':'glint',source.kind==='land'?ground:hand,.38,'folium',4);return plan;}
  add('cutLine',point,source.size||1.1,'folium',1,{rotation,lifeMs:600});
  add('slashArc',point,1.12,'folium',1,{rotation,lifeMs:230});
  add('splat',point,.2,'ink',3,{dir:along});
  if(source.kind==='click'){
   add('cutLine',point,1.25,'gold',1,{rotation:rotation+Math.PI/2,lifeMs:480});
   add('impactRay',point,.75,'folium',6,{dir:[0,1,0]});plan.punch=4;plan.kick=2.8;plan.flare=true;plan.impact=true;
  }else plan.kick=.8;
 }
 return plan;
}
/** Relative timestamps; the renderer samples the actual world-space hands at
 * these instants. A defensive receipt must never request an offensive slash. */
export function duelVisualCues(beat:DuelBeat):DuelVisualCue[] {
  const t=duelTiming(beat),cues:DuelVisualCue[]=[];
  if(isWeaponSuper(beat.action)) {
    cues.push(...superVisualBeats(beat.action).map(source=>({at:abilityEventAt(beat.action as SuperAction,source.time)*1000,side:0 as const,kind:'super' as const,source})));
    // Lower-cost techniques can have a normal answer after their authored
    // clip; their own visual sequence must not swallow that later contact.
    if(!beat.noReply){
      if(beat.received>0)for(const at of t.enemyContacts)cues.push({at,side:1,kind:'cut'});
      if(beat.techniqueBlocked||beat.shieldBlocked)for(const at of t.enemyContacts)cues.push({at,side:0,kind:'parry'});
    }
    if(beat.healed>0)cues.push({at:t.heroContact.at(-1)!,side:0,kind:'heal'});
    if(beat.foeHealed)cues.push({at:t.enemyContact,side:1,kind:'heal'});
    return cues.sort((a,b)=>a.at-b.at);
  }
  if(beat.weapon==='staff'&&beat.action==='strike')cues.push({at:Math.max(0,t.heroContact[0]-240),side:0,kind:'charge'});
  if(beat.action==='guard') {
    for(const at of t.heroContact)cues.push({at,side:0,kind:'parry'});
  } else {
    if(beat.dealt>0||beat.armourBlocked)for(const at of t.heroContact)cues.push({at,side:0,kind:beat.dealt===0?'parry':'cut'});
    if(beat.received>0)for(const at of t.enemyContacts)cues.push({at,side:1,kind:beat.intent==='guard'?'parry':'cut'});
  }
  if(beat.healed>0)cues.push({at:t.heroContact.at(-1)!,side:0,kind:'heal'});
  if(beat.foeHealed)cues.push({at:t.enemyContact,side:1,kind:'heal'});
  return cues.sort((a,b)=>a.at-b.at);
}
